import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import './RichTextAttribute.less'
import { RichTextAttributeProps } from 'Types/Attribute.types'
import { RichTextEditor } from 'Components/common/RichTextEditor/RichTextEditor'
import Button from 'antd/lib/button'
import { appConfig } from 'Config/app-config'
import { WindowWithWSCType, WscInstanceType } from 'Plugins/Grammer/Grammar.plugin'
import { isEmpty } from 'lodash'
import Flex from 'antd/lib/flex'
import EditFilled from '@ant-design/icons/lib/icons/EditFilled'
import ArrowsAltOutlined from '@ant-design/icons/lib/icons/ArrowsAltOutlined'

const options = ['addWord', 'ignoreAll', 'toggle']

const RichTextAttribute: React.FC<RichTextAttributeProps> = props => {
    const [isCollapsed, setIsCollapsed] = useState(true)
    const [isDisabled, setIsDisabled] = useState(true)
    const [isEditorReady, setIsEditorReady] = useState(false)
    const [lastUpdatedContent, setLastUpdatedContent] = useState(props.value)
    const [grammarProblemsCount, setGrammarProblemsCount] = useState(0)
    const richTextEditorRef = useRef<{ getContainer: () => HTMLIFrameElement | null }>(null)

    // When we use the rich text editor in attributes, specially in turn level attributes, we need to use a unique id for the editor to avoid conflicts with other editors.
    const editorId = useMemo(() => props.id + '-' + Date.now().toString(), [props.id])

    useEffect(() => {
        setIsEditorReady(false)
    }, [isCollapsed])

    useEffect(() => {
        if (
            isCollapsed ||
            props.readonly ||
            !isEditorReady ||
            !props.grammar.isEnabled ||
            !richTextEditorRef.current?.getContainer()
        ) {
            return
        }

        if (props.grammar.proofreadDialog) {
            options.push('proofreadDialog')
        }
        if (props.grammar.settings) {
            options.push('settings')
        }

        const instance = (window as WindowWithWSCType).WEBSPELLCHECKER.init({
            container: richTextEditorRef.current?.getContainer() as HTMLIFrameElement,
            lang: props.grammar.lang ?? 'en_US',
            actionItems: options,
            autoSearch: true,
            autoDestroy: true,
            autocorrect: false,
            autocomplete: true,
            withCredentials: true,
            enforceAI: true,
            serviceId: 'CustomerId',
            serviceHost: appConfig.expertsApiBaseUrl.replace(/https?:\/\//i, '').replace(/\/$/, ''), //Get URL without 'http' or 'https' string and remove trailing slash
            enableBadgeButton: false,
            customDictionaryIds: '',
            servicePath: 'v1/plugin-management/grammar-checker',
            // servicePort: '443',
            serviceProtocol: 'https',
            onAddWordToUserDictionary: () => {
                // Make the calls asynchronous as the instance doesn't reflect the updated problems count immediately
                Promise.resolve().then(() => getProblems(instance))
            },
        })

        if (instance) {
            getProblems(instance)

            instance.subscribe('problemCheckEnded', () => {
                getProblems(instance)
            })
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEditorReady, props.readonly])

    const getProblems = (instance: WscInstanceType) => {
        if (!instance) return
        const problemsCount = instance.getProblemsCount()

        setGrammarProblemsCount(problemsCount)
    }

    const isContentEmpty = useMemo(() => {
        return isEmpty(props.value?.trim())
    }, [props.value])

    const onClickEditContent = useCallback(() => {
        setLastUpdatedContent(props.value)
        setIsDisabled(false)
        setIsCollapsed(false)
    }, [props.value])

    const onClickViewContent = useCallback(() => {
        setLastUpdatedContent(props.value)
        setIsCollapsed(false)
    }, [props.value])

    const onClickCancel = useCallback(() => {
        props.onChange(lastUpdatedContent)
        setIsDisabled(true)
        setIsCollapsed(true)
    }, [lastUpdatedContent, props])

    const onClickSave = useCallback(() => {
        setIsDisabled(true)
        setIsCollapsed(true)
    }, [])

    return (
        <>
            <div
                className={`attribute-main ${props.className} richtext-attribute`}
                style={{ width: props.width ?? '90%' }}
                data-testid="rich-text-wrapper"
            >
                <span className="attribute-main-title">{props.label}</span>
                <div
                    className={
                        'rich-text-attribute-container' +
                        (isCollapsed ? ' collapsed' : '') +
                        (isContentEmpty ? ' empty' : '')
                    }
                >
                    <div className="richtext-editor-container">
                        <RichTextEditor
                            id={editorId}
                            value={props.value ?? ''}
                            onChange={content => {
                                if (props.onChange) {
                                    props.onChange(content)
                                }
                            }}
                            readonly={(props.readonly ?? false) || isDisabled}
                            installedPlugins={[]}
                            keyBindingActions={() => {}}
                            isPasteFunctionalityEnabled={true}
                            label={props.label ?? ''}
                            height={isCollapsed ? 0 : window.innerHeight - 155}
                            ref={richTextEditorRef}
                            onEditorReady={() => setIsEditorReady(true)}
                        />
                        {!isCollapsed && (
                            <div className="richtext-attribute-container-actions">
                                <Button onClick={onClickCancel}>
                                    {isDisabled ? 'Close' : 'Cancel'}
                                </Button>
                                {isDisabled ? (
                                    <></>
                                ) : (
                                    <Button type="primary" onClick={onClickSave}>
                                        Save
                                    </Button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                {!isContentEmpty && (
                    <Flex
                        justify="space-between"
                        vertical={false}
                        align="center"
                        className="richtext-attribute-collapsed-actions"
                    >
                        <Flex vertical={false} align="center">
                            <p
                                className={
                                    'grammar-issue-count' +
                                    (grammarProblemsCount > 0 ? ' has-issues' : '')
                                }
                            >
                                {grammarProblemsCount > 0 ? (
                                    <>Grammar Issue Count: {grammarProblemsCount}</>
                                ) : (
                                    ''
                                )}
                            </p>
                        </Flex>
                        <Flex vertical={false} align="center" gap={10}>
                            <Button
                                shape="circle"
                                onClick={onClickViewContent}
                                className={'edit-content-button'}
                            >
                                <ArrowsAltOutlined />
                            </Button>
                            {!props.readonly && (
                                <Button
                                    shape="circle"
                                    onClick={onClickEditContent}
                                    className={'edit-content-button'}
                                >
                                    <EditFilled />
                                </Button>
                            )}
                        </Flex>
                    </Flex>
                )}
                {isContentEmpty && (
                    <Button
                        disabled={props.readonly}
                        type="primary"
                        onClick={onClickEditContent}
                        className={'add-content-button'}
                    >
                        Add Content
                    </Button>
                )}
            </div>
        </>
    )
}

export default RichTextAttribute
